import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import * as iconClose from '~/assets/icons/icon_close.svg';
import * as iconCloseGray from '~/assets/icons/icon_close_gray.svg';
import OuterContainer from '~/components/Modal/OuterContainer';
import { ResponsiveBreakPoints } from '~/constants/responsive';
import useStackablePortal, { isTopOfStack } from '~/hooks/useStackablePortal';
import * as React from 'react';
/* tslint:disable-next-line:import-name */
import ClickOutside from 'react-click-outside';
import { useDispatch } from 'react-redux';
import { CSSLengthUnit, cssLengthUnits } from '~/utils/css';

export type ModalSize = 'full' | 'xl' | 'lg' | 'mdl' | 'md' | 'sm' | 'xs' | 'auto';

const innerContainerSizesMapping = {
  xl: '1000px',
  lg: '800px',
  mdl: '640px',
  md: '600px',
  sm: '500px',
  xs: '440px',
  auto: 'auto',
};

interface InnerContainerProps {
  size: ModalSize;
  minHeight?: CSSLengthUnit;
  verticallyCentered: boolean;
  scrollContent: boolean;
  isDialog: boolean;
}

const InnerContainer = styled(ClickOutside, {
  shouldForwardProp: isPropValid,
})<InnerContainerProps>`
  position: relative;
  background-color: white;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  box-shadow: 0px 4px 25px 4px rgba(0, 0, 0, 0.3);
  text-align: left;
  overflow: auto;

  ${(props) => (props.verticallyCentered ? `top: 50%; transform: translateY(-50%)` : '')};

  ${(props) =>
    props.isDialog
      ? `
    width: 94%;
    margin: 50% 3% 0 3%;
    border-radius: 4px;
    `
      : `
    width: 100vw;
    height: 100vh;
  `}

  @media screen and (min-width: ${ResponsiveBreakPoints.ExtraSmallMobile}px) {
    border-radius: ${(props) => (props.size === 'full' ? '0px' : '4px')};
    min-height: ${(props) => props.minHeight};
    margin: ${(props) => (props.verticallyCentered ? '0 auto' : '1rem')};
    ${(props) => {
      switch (props.size) {
        case 'full':
          return `
            height: 100%;
            width: 100%;
            overflow-y: scroll;
            margin: 0;
          `;
        case 'auto':
          return `
              height: auto;
              width: calc(100% - 2rem);
            `;
        default:
          return `
            height: calc(100% - 2rem); 
            width: calc(100% - 2rem); 
          `;
      }
    }}
  }

  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    overflow: visible;
    border-radius: ${(props) => (props.size === 'full' ? '0px' : '4px')};
    min-height: ${(props) => props.minHeight};
    margin: ${(props) => (props.verticallyCentered ? '0 auto' : '100px auto 30px')};
    ${(props) =>
      props.size === 'full'
        ? `
        height: 100%;
        width: 100%;
        overflow-y: scroll;
        margin: 0;
        `
        : `
        height: ${props.scrollContent ? '60vh' : 'auto'};
        width: ${
          props.size === 'auto'
            ? innerContainerSizesMapping['md']
            : innerContainerSizesMapping[props.size]
        };`}
  }
`;

const CloseIcon = styled('img')`
  position: absolute;
  top: -35px;
  right: 0;
  color: white;
  font-size: 16px;
  cursor: pointer;
  z-index: 1;
`;

const FullSizeContainerCloseIcon = styled(CloseIcon)<{ hasNoPaddingHeader?: boolean }>`
  top: 27px;
  right: 30px;
  @media screen and (min-width: ${ResponsiveBreakPoints.SmallMobile}px) {
    top: 30px;
  }
  ${(props) => (props.hasNoPaddingHeader ? 'top: 22px' : undefined)}
`;

const ResponsiveCloseIconContainer = styled('div')<{ isDialog?: boolean }>`
  display: ${(props) => (props.isDialog ? 'none' : 'inherit')};
  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    display: none;
  }
`;

InnerContainer.defaultProps = {
  size: 'md',
  minHeight: cssLengthUnits.rem(12.5),
};

export interface ModalProps {
  isOpen: boolean;
  onClose?: (evt?) => void;
  onClickedOut?: () => void;
  size?: ModalSize;
  closeOnClickOutside?: boolean;
  showCloseIcon?: boolean;
  verticallyCentered?: boolean;
  scrollContent?: boolean;
  isDialog?: boolean;
  className?: string;
  closeIconPendoTag?: string;
  closeIconE2ETag?: string;
  minHeight?: CSSLengthUnit;
  alwaysHideCloseIcon?: boolean; // Only use if there's another way to close the modal
  children?: React.ReactNode;
  dataAnalyticsContext?: string;
  hasNoPaddingHeader?: boolean;
  zIndex?: number;
  backgroundColor?: string;
}

const Modal = (props: ModalProps) => {
  const dispatch = useDispatch();

  const closeIcon = props.showCloseIcon ? (
    <CloseIcon
      onClick={props.onClose}
      data-analytics={
        props.dataAnalyticsContext ? `${props.dataAnalyticsContext}-click-close` : undefined
      }
      data-e2e={props.closeIconE2ETag}
      data-pendo={props.closeIconPendoTag}
      src={iconClose}
    />
  ) : null;

  const portalKey = useStackablePortal(!!props.isOpen);

  const handleClose = (evt?: React.MouseEvent) => {
    if (isTopOfStack(portalKey)) {
      if (props.dataAnalyticsContext) {
        dispatch(sendTrackEvent(`${props.dataAnalyticsContext}-click-outside`));
      }
      if (props.closeOnClickOutside && props.onClose) {
        props.onClose(evt);
      }
      if (props.closeOnClickOutside && props.onClickedOut) {
        props.onClickedOut();
      }
    }
  };

  const renderFullSizeContainerCloseIcon = () =>
    props.alwaysHideCloseIcon ? null : (
      <FullSizeContainerCloseIcon
        hasNoPaddingHeader={props.hasNoPaddingHeader}
        data-analytics={
          props.dataAnalyticsContext ? `${props.dataAnalyticsContext}-click-close` : undefined
        }
        onClick={props.onClose}
        data-pendo={props.closeIconPendoTag}
        src={iconCloseGray}
      />
    );

  const shouldShowCloseIcon = props.size === 'full' && props.showCloseIcon;

  return props.isOpen ? (
    <OuterContainer
      {...(props.zIndex && { zIndex: props.zIndex })}
      {...(props.backgroundColor && { backgroundColor: props.backgroundColor })}
    >
      <InnerContainer
        onClickOutside={handleClose}
        size={props.size}
        verticallyCentered={props.verticallyCentered}
        scrollContent={props.scrollContent}
        isDialog={props.isDialog}
        className={props.className}
        minHeight={props.minHeight}
      >
        {shouldShowCloseIcon ? (
          renderFullSizeContainerCloseIcon()
        ) : (
          // Cheap media query
          <ResponsiveCloseIconContainer isDialog={props.isDialog}>
            {renderFullSizeContainerCloseIcon()}
          </ResponsiveCloseIconContainer>
        )}
        {closeIcon}
        {props.children}
      </InnerContainer>
    </OuterContainer>
  ) : null;
};

Modal.defaultProps = {
  isOpen: false,
  closeOnClickOutside: true,
};

export const MobileModal = styled(Modal)`
  position: fixed;
  top: 0;
  bottom: 0;

  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    position: relative;
    top: auto;
    bottom: auto;
  }
`;

export default Modal;
